<template>
  <section
    class="live-report"
    aria-label="Gerçek zamanlı rapor"
    :aria-busy="loading"
  >
    <div class="live-report__heading">
      <div>
        <h2>Gerçek zamanlı</h2><p class="text-caption text--secondary mb-0">
          Son 30 dakika · 30 saniyede bir yenilenir<span v-if="report"> · {{ time(report.generatedAt) }} (TR)</span>
        </p>
      </div>
      <v-btn
        small
        outlined
        color="primary"
        :loading="loading"
        @click="fetchReport"
      >
        Canlı raporu yenile
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      dense
      outlined
      role="alert"
    >
      {{ error }}
    </v-alert>
    <v-card
      outlined
      class="admin-table-card live-report__summary"
    >
      <div
        v-for="metric in metrics"
        :key="metric.label"
        class="live-report__metric"
      >
        <span class="text-caption text--secondary">{{ metric.label }}</span><strong>{{ report ? number(metric.value) : '—' }}</strong><span class="text-caption text--secondary">{{ metric.note }}</span>
      </div>
    </v-card>
    <p class="text-caption text--secondary mb-0">
      Yakın zamanlı aktiviteyi gösterir; cihazın şu anda çevrimiçi olduğunu veya hâlâ aynı ekranda kaldığını göstermez. Sayfa kullanıcıları son 30 dakikadaki tekil ziyaretçilerdir.
    </p>
    <v-tabs
      v-model="tab"
      class="live-report__tabs"
      show-arrows
      aria-label="Gerçek zamanlı rapor görünümü"
    >
      <v-tab>Aktivite özeti</v-tab><v-tab>Gerçek zamanlı sayfalar</v-tab><v-tab>Son aktif kullanıcılar</v-tab>
    </v-tabs>
    <div
      v-if="tab === 0"
      class="live-report__overview"
    >
      <v-card
        outlined
        class="admin-table-card live-report__chart"
      >
        <div class="live-report__heading">
          <div><h3>Dakika bazında aktivite</h3><span class="text-caption text--secondary">Boş dakikalar sıfır gösterilir.</span></div>
          <v-btn-toggle
            v-model="chartMetric"
            mandatory
            dense
            borderless
            color="primary"
            aria-label="Canlı grafik metriği"
          >
            <v-btn
              small
              value="activeUsers"
            >
              Kullanıcı
            </v-btn><v-btn
              small
              value="events"
            >
              Event
            </v-btn>
          </v-btn-toggle>
        </div>
        <v-skeleton-loader
          v-if="loading && !report"
          type="image"
          class="ma-4"
        ></v-skeleton-loader>
        <vue-apex-charts
          v-else-if="report"
          type="bar"
          height="255"
          :options="chartOptions"
          :series="chartSeries"
        ></vue-apex-charts>
        <div
          v-else
          class="live-report__empty"
        >
          Canlı grafik kullanılamıyor.
        </div>
      </v-card>
      <v-card
        outlined
        class="admin-table-card live-report__breakdown"
      >
        <div class="live-report__heading">
          <h3>Platform dağılımı</h3><span class="text-caption text--secondary">Event adedi</span>
        </div>
        <div
          v-for="platform in platforms"
          :key="platform.name"
          class="live-report__platform"
        >
          <div><span>{{ platform.name === 'ios' ? 'iOS' : platform.name === 'android' ? 'Android' : platform.name === 'unknown' ? 'Bilinmiyor' : platform.name }}</span><strong>{{ number(platform.events) }}</strong></div>
          <v-progress-linear
            :value="report.totals.events ? 100 * platform.events / report.totals.events : 0"
            color="primary"
            height="5"
            rounded
          ></v-progress-linear>
        </div>
        <div
          v-if="!platforms.length"
          class="live-report__empty"
        >
          {{ error ? 'Platform dağılımı yüklenemedi.' : 'Platform aktivitesi yok.' }}
        </div>
      </v-card>
    </div>
    <v-card
      v-if="tab !== 2"
      outlined
      class="admin-table-card"
    >
      <div class="live-report__heading">
        <div><h3>{{ tab === 1 ? 'Gerçek zamanlı sayfalar ve ekranlar' : 'Şu sıralar kullanılan ekranlar' }}</h3><span class="text-caption text--secondary">Görüntülemeye göre ilk 10 ekran · Son 30 dakika</span></div><v-btn
          v-if="tab === 0"
          text
          small
          color="primary"
          @click="tab = 1"
        >
          Sayfaları incele
        </v-btn>
      </div>
      <v-data-table
        loading-text="Ekran aktivitesi yükleniyor…"
        :headers="screenHeaders"
        :items="screens"
        :loading="loading"
        :items-per-page="10"
        hide-default-footer
        :mobile-breakpoint="760"
        class="admin-data-table"
        @click:row="inspectScreen"
      >
        <template #[`item.key`]="{ item }">
          <button
            type="button"
            class="live-report__name"
            @click.stop="inspectScreen(item)"
          >
            {{ label(item.key) }}
          </button><div class="text-caption text--secondary">
            {{ item.key }}
          </div>
        </template>
        <template #[`item.views`]="{ item }">
          {{ number(item.views) }}
        </template>
        <template #[`item.activeUsers`]="{ item }">
          {{ number(item.activeUsers) }}
        </template>
        <template #[`item.events`]="{ item }">
          {{ number(item.events) }}
        </template>
        <template #[`item.lastSeen`]="{ item }">
          {{ time(item.lastSeen) }}
        </template>
        <template #no-data>
          <div class="live-report__empty">
            {{ error ? 'Sayfalar yüklenemedi.' : 'Son 30 dakikada ekran aktivitesi yok.' }}
          </div>
        </template>
      </v-data-table>
    </v-card>
    <div
      v-if="tab === 0"
      class="live-report__event-grid"
    >
      <v-card
        outlined
        class="admin-table-card"
      >
        <div class="live-report__heading">
          <h3>Öne çıkan eventler</h3><span class="text-caption text--secondary">İlk 10</span>
        </div>
        <button
          v-for="event in events"
          :key="event.name"
          type="button"
          class="live-report__event"
          @click="inspect('events', event.name)"
        >
          <span>{{ event.name }}</span><strong>{{ number(event.events) }}</strong>
        </button>
        <div
          v-if="!events.length"
          class="live-report__empty"
        >
          {{ error ? 'Event dağılımı yüklenemedi.' : 'Son 30 dakikada event yok.' }}
        </div>
      </v-card>
      <v-card
        outlined
        class="admin-table-card live-report__people-note"
      >
        <span class="text-caption text--secondary">KULLANICI AKTİVİTESİ</span><h3>Kim, en son nerede etkileşimde bulundu?</h3><p class="text-body-2 text--secondary">
          Kullanıcı ID ve e-postayla son gözlenen ekranı ve eventi inceleyin. Satırdan aynı zaman penceresindeki event geçmişine geçin.
        </p><v-btn
          outlined
          small
          color="primary"
          @click="tab = 2"
        >
          Son aktif kullanıcıları aç
        </v-btn>
      </v-card>
    </div>
    <v-card
      v-if="tab === 2"
      outlined
      class="admin-table-card"
    >
      <div class="live-report__heading">
        <div>
          <h3>Son aktif kullanıcılar</h3><p class="text-caption text--secondary mb-0">
            Son 30 dakikada aktivitesi olan en güncel 20 kullanıcı. Son gözlenen etkileşim listelenir; canlı ekran konumu değildir.
          </p>
        </div>
      </div>
      <v-data-table
        loading-text="Kullanıcı aktivitesi yükleniyor…"
        :headers="userHeaders"
        :items="users"
        :loading="loading"
        :items-per-page="20"
        item-key="userId"
        hide-default-footer
        :mobile-breakpoint="760"
        class="admin-data-table"
        @click:row="inspectUser"
      >
        <template #[`item.userId`]="{ item }">
          <button
            type="button"
            class="live-report__name"
            @click.stop="inspectUser(item)"
          >
            #{{ item.userId }} {{ item.userName }}
          </button><div class="text-caption text--secondary">
            {{ item.email || 'E-posta eşleşmesi yok' }}
          </div>
        </template>
        <template #[`item.screenName`]="{ item }">
          {{ label(item.screenName) }}<div class="text-caption text--secondary">
            {{ item.eventName }}
          </div>
        </template>
        <template #[`item.lastSeen`]="{ item }">
          {{ time(item.lastSeen) }}
        </template>
        <template #no-data>
          <div class="live-report__empty">
            {{ error ? 'Kullanıcılar yüklenemedi.' : 'Filtrelere uyan yakın zamanlı kullanıcı aktivitesi yok.' }}
          </div>
        </template>
      </v-data-table>
    </v-card>
  </section>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import { getAccessToken } from '@/utils/panelSession'
import { eventParams, formatEventDate, screenLabel } from '@/utils/analyticsEvents'

export default {
  components: { VueApexCharts },
  props: { filters: { type: Object, required: true }, refreshKey: { type: Number, default: 0 } },
  data: () => ({
    report: null,
    loading: false,
    error: '',
    tab: 0,
    chartMetric: 'activeUsers',
    request: 0,
    cancel: null,
    timer: null,
    screenHeaders: [{ text: 'Sayfa / ekran', value: 'key' }, { text: 'Görüntüleme', value: 'views', align: 'end' }, { text: 'Aktif kullanıcı', value: 'activeUsers', align: 'end' }, { text: 'Event', value: 'events', align: 'end' }, { text: 'Son aktivite (TR)', value: 'lastSeen', align: 'end' }].map(item => ({ ...item, sortable: false })),
    userHeaders: [{ text: 'Kullanıcı', value: 'userId' }, { text: 'Son gözlenen ekran / event', value: 'screenName' }, { text: 'Platform', value: 'platform' }, { text: 'Son aktivite (TR)', value: 'lastSeen', align: 'end' }].map(item => ({ ...item, sortable: false })),
  }),
  computed: {
    querySignature() { return JSON.stringify({ filters: this.filters, refresh: this.refreshKey }) },
    screens() { return this.report?.screens || [] },
    events() { return this.report?.events || [] },
    platforms() { return this.report?.platforms || [] },
    users() { return this.report?.users || [] },
    metrics() {
      const t = this.report?.totals || {}

      return [{ label: 'Aktif kullanıcı', value: t.activeUsers, note: '30 dakikada tekil kullanıcı' }, { label: 'Ekran görüntüleme', value: t.views, note: 'SCREEN_VIEW eventleri' }, { label: 'Event', value: t.events, note: 'Tüm eşleşen eventler' }, { label: 'Oturum', value: t.sessions, note: 'Tekil oturum ID' }]
    },
    chartSeries() { return [{ name: this.chartMetric === 'events' ? 'Event' : 'Aktif kullanıcı', data: (this.report?.minutes || []).map(minute => minute[this.chartMetric]) }] },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false }, animations: { enabled: false }, fontFamily: 'inherit', background: 'transparent',
        },
        theme: { mode: this.$vuetify.theme.dark ? 'dark' : 'light' },
        colors: [this.$vuetify.theme.currentTheme.primary],
        plotOptions: { bar: { columnWidth: '60%', borderRadius: 2 } },
        dataLabels: { enabled: false },
        grid: { borderColor: this.$vuetify.theme.dark ? '#334155' : '#e2e8f0', strokeDashArray: 3, padding: { left: 12, right: 24 } },
        xaxis: {
          categories: (this.report?.minutes || []).map(minute => minute.minute), tickAmount: 5, labels: { rotate: 0, hideOverlappingLabels: true, formatter: value => this.time(value).slice(0, 5) }, axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { formatter: value => this.number(Math.round(value)) } },
        tooltip: { x: { formatter: (_, context) => `${this.time(this.report?.minutes[context.dataPointIndex]?.minute).slice(0, 5)} (TR)` } },
      }
    },
  },
  watch: {
    querySignature(next, previous) { this.fetchReport(JSON.stringify(JSON.parse(next).filters) !== JSON.stringify(JSON.parse(previous).filters)) },
  },
  mounted() { this.fetchReport(); this.timer = setInterval(this.poll, 30000); document.addEventListener('visibilitychange', this.poll) },
  beforeDestroy() { this.request += 1; if (this.cancel) this.cancel.cancel(); clearInterval(this.timer); document.removeEventListener('visibilitychange', this.poll) },
  methods: {
    async fetchReport(clear = false) {
      this.request += 1
      const { request } = this
      if (this.cancel) this.cancel.cancel()
      this.cancel = axios.CancelToken.source(); this.loading = true; this.error = ''
      if (clear) this.report = null
      const params = eventParams(this.filters)
      delete params.from; delete params.to
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/analytics/events/realtime-report`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` }, timeout: 30000, cancelToken: this.cancel.token, params,
        })
        if (request === this.request) this.report = response.data.data
      } catch (error) {
        if (request !== this.request || axios.isCancel(error)) return
        this.report = null; this.error = error.response?.data?.message || 'Canlı rapor güncellenemedi. Yeniden deneyin.'
      } finally { if (request === this.request) this.loading = false }
    },
    poll() { if (!document.hidden && !this.loading) this.fetchReport() },
    inspect(dimension, key) {
      this.$emit('inspect', {
        dimension, key: String(key), windowStartedAt: this.report?.windowStartedAt, generatedAt: this.report?.generatedAt,
      })
    },
    inspectScreen(row) { this.inspect('screens', row.key) },
    inspectUser(row) { this.inspect('users', row.userId) },
    label: screenLabel,
    time(value) { return formatEventDate(value, true) },
    number(value) { return new Intl.NumberFormat('tr-TR').format(Number(value || 0)) },
  },
}
</script>

<style lang="scss" scoped>
.live-report {
  display: flex; flex-direction: column; gap: 18px; min-width: 0;
  --report-heading: #172033;
  h2, h3, strong { color: var(--report-heading); }
  h2 { margin: 0 0 4px; font-size: 21px; } h3 { margin: 0; font-size: 15px; }
  &__heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
  .v-card &__heading { padding: 20px; }
  &__summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); box-shadow: none !important; }
  &__metric { display: flex; flex-direction: column; gap: 8px; padding: 22px; border-right: 1px solid rgba(100,116,139,.16); }
  &__metric:last-child { border: 0; } &__metric strong { font-size: 32px; line-height: 1.3; font-variant-numeric: tabular-nums; }
  &__overview { display: grid; grid-template-columns: minmax(0, 2fr) minmax(250px, 1fr); gap: 20px; }
  &__event-grid { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 20px; }
  &__platform { padding: 0 20px 25px; } &__platform > div:first-child { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px; }
  &__name { font-weight: 600; color: inherit; text-align: left; overflow-wrap: anywhere; }
  &__name:hover, &__name:focus-visible, &__event:hover, &__event:focus-visible { color: var(--v-primary-base); }
  &__event { display: flex; justify-content: space-between; gap: 14px; width: 100%; padding: 13px 20px; text-align: left; color: inherit; font-size: 13px; border-top: 1px solid rgba(100,116,139,.1); }
  &__event span { overflow-wrap: anywhere; }
  &__people-note { display: flex; flex-direction: column; gap: 18px; align-items: flex-start; justify-content: center; padding: 30px; }
  &__people-note h3 { font-size: 21px; line-height: 1.5; }
  &__empty { padding: 32px; text-align: center; font-size: 13px; }
  ::v-deep td { padding-top: 14px !important; padding-bottom: 14px !important; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; cursor: pointer; }
  @media (max-width: 760px) { &__summary { grid-template-columns: 1fr 1fr; } &__metric { padding: 16px; } &__overview, &__event-grid { grid-template-columns: minmax(0, 1fr); } }
}
.theme--dark .live-report { --report-heading: #f1f5f9; }
</style>
